import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { sortBy, toLower, trim } from "lodash";
import GoogleAutoComplete from "../GoogleAutoComplete";
import { fetchAddressCheckoutData } from "../../main-component/PageFunctions/checkoutFunctions";
import { ApiCallStatus } from "../../utils/constants";
import { inputSx } from "./constants";

const INITIAL_ADDRESS_FORM = {
  countryId: "",
  streetAddress: "",
  suburb: "",
  city: "",
  postalCode: "",
  provinceId: "",
};

const EMPTY_COORDINATES = { lat: null, lng: null };

const DeliveryAddressModal = ({ open, onClose, onConfirm }) => {
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [googleAddressValue, setGoogleAddressValue] = useState(null);
  const [googlePlaceDetailsResponse, setGooglePlaceDetailsResponse] =
    useState(null);
  const [addressForm, setAddressForm] = useState(INITIAL_ADDRESS_FORM);

  const [addressDataCallStatus, setAddressDataCallStatus] = useState(
    ApiCallStatus.NotStarted,
  );
  const [fetchGooglePlaceDetailsCallStatus, setFetchGooglePlaceDetailsCallStatus] =
    useState(ApiCallStatus.NotStarted);

  useEffect(() => {
    if (open && addressDataCallStatus === ApiCallStatus.NotStarted) {
      fetchAddressCheckoutData({
        setAddressCheckoutInfo: (data) => {
          const alphabetical = sortBy(data?.countries ?? [], ({ name }) => name);
          const withSouthAfricaFirst = sortBy(alphabetical, ({ name }) =>
            name === "South Africa" ? 0 : 1,
          );
          setCountries(withSouthAfricaFirst);
          setProvinces(data?.provinces ?? []);
        },
        setStatusInProgress: () =>
          setAddressDataCallStatus(ApiCallStatus.InProgress),
        setStatusSuccess: () =>
          setAddressDataCallStatus(ApiCallStatus.Succeeded),
        setStatusFailed: () => setAddressDataCallStatus(ApiCallStatus.Failed),
        setStatusError: () => setAddressDataCallStatus(ApiCallStatus.Error),
      });
    }
  }, [open, addressDataCallStatus]);

  useEffect(() => {
    if (
      !open &&
      (addressDataCallStatus === ApiCallStatus.Failed ||
        addressDataCallStatus === ApiCallStatus.Error)
    ) {
      setAddressDataCallStatus(ApiCallStatus.NotStarted);
    }
  }, [open, addressDataCallStatus]);

  useEffect(() => {
    if (
      fetchGooglePlaceDetailsCallStatus !== ApiCallStatus.Succeeded ||
      !googlePlaceDetailsResponse
    ) {
      return;
    }

    const streetNumber = googlePlaceDetailsResponse?.streetNumber ?? "";
    const route = googlePlaceDetailsResponse?.route ?? "";
    const sublocality = googlePlaceDetailsResponse?.sublocality ?? "";
    const locality = googlePlaceDetailsResponse?.locality ?? "";
    const province = googlePlaceDetailsResponse?.province ?? "";
    const country = googlePlaceDetailsResponse?.country ?? "";
    const postalCode = googlePlaceDetailsResponse?.postalCode ?? "";

    const foundProvince = provinces.find(
      (x) => toLower(x.name) === trim(toLower(province)),
    );
    const foundCountry = countries.find(
      (x) => toLower(x.name) === trim(toLower(country)),
    );

    setAddressForm((prev) => ({
      ...prev,
      streetAddress: trim(`${streetNumber} ${route}`),
      suburb: trim(sublocality),
      city: trim(locality),
      postalCode: trim(postalCode),
      provinceId: foundProvince ? foundProvince.provinceId : prev.provinceId,
      countryId: foundCountry ? foundCountry.countryId : prev.countryId,
    }));
  }, [
    fetchGooglePlaceDetailsCallStatus,
    googlePlaceDetailsResponse,
    countries,
    provinces,
  ]);

  const handleFieldChange = (key, value) => {
    setAddressForm((prev) => ({ ...prev, [key]: value }));
  };

  const placeDetailsError =
    fetchGooglePlaceDetailsCallStatus === ApiCallStatus.Failed ||
    fetchGooglePlaceDetailsCallStatus === ApiCallStatus.Error
      ? "We could not look up that address. Please enter your address details below."
      : null;

  const composedAddress = [
    addressForm.streetAddress,
    addressForm.suburb,
    addressForm.city,
    addressForm.postalCode,
    provinces.find((x) => x.provinceId === addressForm.provinceId)?.name,
    countries.find((x) => x.countryId === addressForm.countryId)?.name,
  ]
    .filter(Boolean)
    .join(", ");

  const handleConfirm = () => {
    onConfirm(
      composedAddress,
      googlePlaceDetailsResponse?.coordinates ?? EMPTY_COORDINATES,
    );
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Delivery Address</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <GoogleAutoComplete
              value={googleAddressValue}
              setValue={setGoogleAddressValue}
              setPlaceDetails={setGooglePlaceDetailsResponse}
              setPlaceDetailsStatus={setFetchGooglePlaceDetailsCallStatus}
            />
            {!!placeDetailsError && (
              <Typography variant="body2" color="error">
                {placeDetailsError}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Country"
              fullWidth
              size="small"
              value={addressForm.countryId}
              onChange={(e) => handleFieldChange("countryId", e.target.value)}
              sx={inputSx}
            >
              {countries.map((option) => (
                <MenuItem key={option.countryId} value={option.countryId}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Street Address"
              fullWidth
              size="small"
              value={addressForm.streetAddress}
              onChange={(e) =>
                handleFieldChange("streetAddress", e.target.value)
              }
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Suburb"
              fullWidth
              size="small"
              value={addressForm.suburb}
              onChange={(e) => handleFieldChange("suburb", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="City"
              fullWidth
              size="small"
              value={addressForm.city}
              onChange={(e) => handleFieldChange("city", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Postal Code"
              fullWidth
              size="small"
              value={addressForm.postalCode}
              onChange={(e) => handleFieldChange("postalCode", e.target.value)}
              sx={inputSx}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Province"
              fullWidth
              size="small"
              value={addressForm.provinceId}
              onChange={(e) => handleFieldChange("provinceId", e.target.value)}
              sx={inputSx}
            >
              {provinces.map((option) => (
                <MenuItem key={option.provinceId} value={option.provinceId}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={!composedAddress}
          onClick={handleConfirm}
        >
          Confirm Address
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeliveryAddressModal;
